import { useEffect, useRef } from 'react';
import { useToast } from '@root/hooks';
import { betSelector } from '@root/store/slices/betSlice';
import { useActions, useTypedSelector } from '@root/store/store';
import type { Bet } from '@root/store/types';
import {
  INSUFFICIENT_BALANCE_ERROR_CODE,
  LIMIT_REACHED_ERROR_CODE,
  ROLL_IN_PROGRESS_ERROR_CODE,
} from '@shared/constants';
import { rejectionCode } from '@shared/lib';
import { useTranslation } from 'react-i18next';

// One message per bet, so debounce a burst into a single toast.
const SUCCESS_NOTIFICATION_DEBOUNCE_MS = 400;

export function useBetNotifications() {
  const { bets, notifiedBetsSuccess, notifiedBetsError } =
    useTypedSelector(betSelector);
  const {
    betNotifiedSuccess,
    betNotifiedError,
    setRollRejectedOpen,
    setShortBalance,
    setRollSubmitted,
  } = useActions();
  const { success, error } = useToast();
  const { t } = useTranslation();

  const pendingSuccessIdsRef = useRef<string[]>([]);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (bets.length === 0) return;

    // Only ACCEPTED is confirmed; PLACED can still come back REJECTED.
    const newlyAcceptedBets = bets.filter(
      (bet: Bet) =>
        bet.status === 'ACCEPTED' &&
        !notifiedBetsSuccess.includes(bet.id) &&
        !pendingSuccessIdsRef.current.includes(bet.id)
    );

    if (newlyAcceptedBets.length > 0) {
      pendingSuccessIdsRef.current = [
        ...pendingSuccessIdsRef.current,
        ...newlyAcceptedBets.map((bet: Bet) => bet.id),
      ];

      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => {
        const ids = pendingSuccessIdsRef.current;
        pendingSuccessIdsRef.current = [];
        success(
          t('Success! Bet accepted'),
          ids.length > 1
            ? t('Your bets have been accepted')
            : t('Your bet has been accepted')
        );
        ids.forEach((id) => betNotifiedSuccess(id));
      }, SUCCESS_NOTIFICATION_DEBOUNCE_MS);
    }

    bets.forEach((bet: Bet) => {
      if (bet.status !== 'REJECTED' || notifiedBetsError.includes(bet.id)) {
        return;
      }

      // Never congratulate a bet that has just bounced.
      pendingSuccessIdsRef.current = pendingSuccessIdsRef.current.filter(
        (id) => id !== bet.id
      );

      const code = rejectionCode(bet.errors);
      const rollInProgress = code === ROLL_IN_PROGRESS_ERROR_CODE;
      // A duplicate means the first roll is still going, so keep it held.
      if (!rollInProgress) setRollSubmitted(false);

      if (code === LIMIT_REACHED_ERROR_CODE) {
        // The player's own limit, so a toast is enough.
        error(
          t('Error! Bet rejected'),
          t('You have reached your betting limit')
        );
      } else if (code === INSUFFICIENT_BALANCE_ERROR_CODE) {
        // Retrying can only fail again. The pause notice brings the deadline.
        setShortBalance(true);
      } else if (!rollInProgress) {
        setRollRejectedOpen(true);
      }

      betNotifiedError(bet.id);
    });
  }, [
    bets,
    notifiedBetsSuccess,
    notifiedBetsError,
    success,
    error,
    betNotifiedSuccess,
    betNotifiedError,
    t,
    setRollRejectedOpen,
    setShortBalance,
    setRollSubmitted,
  ]);

  useEffect(
    () => () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    },
    []
  );
}
